import { useState } from "react"
import { PopoverClose } from "@radix-ui/react-popover"
import { CircleAlertIcon, CopyIcon, PencilIcon, PlusIcon, Trash2Icon } from "lucide-react"

import { cn } from "@/utils"
import { Input } from "@/components/ui/input"
import { Button, buttonVariants } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"

interface LabelsSidebarProps {
  names: string[]
  selectedName: string | null
  invalidCounts: Record<string, number>
  canWrite: boolean
  canDelete: boolean
  onSelect: (name: string) => void
  onAdd: (name: string) => string | null
  onRename: (oldName: string, newName: string) => string | null
  onDuplicate: (name: string) => void
  onDelete: (name: string) => void
}

export function LabelsSidebar({
  names,
  selectedName,
  invalidCounts,
  canWrite,
  canDelete,
  onSelect,
  onAdd,
  onRename,
  onDuplicate,
  onDelete,
}: LabelsSidebarProps) {
  const [name, setName] = useState("")
  const [error, setError] = useState<string | null>(null)

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    const addError = onAdd(name)
    setError(addError)
    if (addError == null) setName("")
  }

  return (
    <aside className="flex min-h-0 w-full min-w-0 shrink-0 flex-col gap-3 rounded-lg border bg-background p-3 lg:w-80 lg:p-4">
      {canWrite && (
        <form onSubmit={submit} className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                setError(null)
              }}
              placeholder="Label name"
              aria-label="Label name"
              aria-invalid={error != null}
            />
            <Button type="submit" size="sm">
              <PlusIcon /> Add
            </Button>
          </div>
          <p role="alert" className="min-h-5 text-sm text-destructive max-lg:[overflow-wrap:anywhere]">
            {error}
          </p>
        </form>
      )}

      <div className="flex max-h-60 min-h-0 flex-col gap-1 overflow-y-auto lg:max-h-none">
        {names.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No labels yet</p>}
        {names.map((labelName) => {
          const invalidCount = invalidCounts[labelName] ?? 0

          return (
            <div
              key={labelName}
              className={cn(
                "flex items-center justify-between gap-1 rounded-md pl-2 hover:bg-accent",
                labelName === selectedName && "bg-accent font-medium",
              )}
            >
              {invalidCount > 0 && (
                <Tooltip>
                  <TooltipTrigger
                    type="button"
                    aria-label="Contains invalid search terms"
                    className={buttonVariants({
                      variant: "ghost",
                      size: "icon",
                      className: "size-6 shrink-0 text-destructive hover:text-destructive",
                    })}
                  >
                    <CircleAlertIcon className="size-4" />
                  </TooltipTrigger>
                  <TooltipContent>
                    {invalidCount} invalid search term{invalidCount === 1 ? "" : "s"}
                  </TooltipContent>
                </Tooltip>
              )}

              <button
                type="button"
                onClick={() => onSelect(labelName)}
                className="flex min-w-0 flex-1 cursor-pointer items-center py-2 text-left text-sm"
              >
                <span className="min-w-0 max-lg:[overflow-wrap:anywhere] lg:truncate">{labelName}</span>
              </button>

              <div className="ml-auto flex shrink-0 items-center">
                {canWrite && (
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-7"
                      aria-label={`Duplicate ${labelName}`}
                      title={`Duplicate ${labelName}`}
                      onClick={() => onDuplicate(labelName)}
                    >
                      <CopyIcon className="size-4" />
                    </Button>
                    <RenameLabelPopover labelName={labelName} onRename={onRename} />
                  </>
                )}
                {canDelete && (
                  <Popover>
                    <PopoverTrigger
                      type="button"
                      aria-label={`Delete ${labelName}`}
                      className={buttonVariants({
                        variant: "ghost",
                        size: "icon",
                        className: "text-red-600 hover:text-red-700",
                      })}
                    >
                      <Trash2Icon />
                    </PopoverTrigger>
                    <PopoverContent
                      align="end"
                      className="max-lg:max-w-(--radix-popover-content-available-width) max-lg:min-w-0"
                    >
                      <p className="text-sm text-pretty">
                        Are you sure you want to delete the label &quot;
                        <span className="font-semibold max-lg:[overflow-wrap:anywhere]">{labelName}</span>
                        &quot;?
                        <span className="mt-1 block text-red-600">
                          Its search terms and classification settings will be removed.
                        </span>
                      </p>
                      <div className="flex flex-wrap items-center justify-end gap-3 pt-4">
                        <PopoverClose type="button" className={buttonVariants({ variant: "outline" })}>
                          Cancel
                        </PopoverClose>
                        <PopoverClose
                          type="button"
                          className={buttonVariants({ variant: "destructive" })}
                          onClick={() => onDelete(labelName)}
                        >
                          Confirm Delete
                        </PopoverClose>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </aside>
  )
}

function RenameLabelPopover({
  labelName,
  onRename,
}: {
  labelName: string
  onRename: (oldName: string, newName: string) => string | null
}) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState(labelName)
  const [error, setError] = useState<string | null>(null)

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen)
    if (nextOpen) {
      setName(labelName)
      setError(null)
    }
  }

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const renameError = onRename(labelName, name)
    setError(renameError)
    if (renameError == null) setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        type="button"
        className={buttonVariants({ variant: "ghost", size: "icon", className: "size-7" })}
        aria-label={`Rename ${labelName}`}
        title={`Rename ${labelName}`}
      >
        <PencilIcon className="size-4" />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 max-lg:max-w-(--radix-popover-content-available-width) max-lg:min-w-0"
      >
        <form onSubmit={submit} className="flex flex-col gap-2">
          <label htmlFor={`rename-label-${labelName}`} className="text-sm font-medium">
            Rename label
          </label>
          <Input
            id={`rename-label-${labelName}`}
            aria-label="New label name"
            value={name}
            onChange={(event) => {
              setName(event.target.value)
              setError(null)
            }}
            aria-invalid={error != null}
            autoFocus
          />
          {error != null && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <PopoverClose asChild>
              <Button type="button" variant="ghost" size="sm">
                Cancel
              </Button>
            </PopoverClose>
            <Button type="submit" size="sm">
              Save
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  )
}
